/**
 * 红线规则配置编辑器共享纯函数。
 *
 * - `splitDy` / `joinDy`：targeting_id / ext_info 中「、」分隔列表的拆分与拼接
 *   （与后端 / 原文件 `|` 表的「、」并列约定一致）。
 * - `parseJsonExt` / `stringifyJsonExt`：legal_date / special_condition 的 ext_info 是 JSON。
 * - `dateIntToIso` / `isoToDateInt`：legal_date 的 holiday / leave_in_lieu 用 yyyyMMdd(int)，
 *   前端 <input type=date> 用 yyyy-MM-dd，二者互转。
 */

/** 「、」分隔串 → 去空白、去空段的字符串数组。 */
export function splitDy(s: string | null | undefined): string[] {
  return (s ?? "")
    .split("、")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/** 字符串数组 → 「、」拼接串。 */
export function joinDy(arr: string[]): string {
  return arr.join("、");
}

/**
 * 「、」分隔串 → 原始段数组（**不**去空、不 trim），用于 excluded_custom 的 names
 * 字段——须与 ids 保持平行索引，空段需保留以维持 id[i] ↔ name[i] 对应。
 */
export function splitDyKeep(s: string | null | undefined): string[] {
  return (s ?? "").split("、");
}

/** 段数组 → 「、」拼接串（**不**去空），与 splitDyKeep 配对。 */
export function joinDyKeep(arr: string[]): string {
  return arr.join("、");
}

/** 解析 ext_info 为 JSON；失败回退 fallback（不抛错，编辑器容错）。 */
export function parseJsonExt<T>(s: string | null | undefined, fallback: T): T {
  if (!s || !s.trim()) return fallback;
  try {
    return JSON.parse(s) as T;
  } catch {
    return fallback;
  }
}

/** 序列化为 JSON 字符串（ensure_ascii 由后端 JSON 列承载，前端原样输出）。 */
export function stringifyJsonExt(obj: unknown): string {
  return JSON.stringify(obj);
}

/** yyyyMMdd(int, 20260619) → yyyy-MM-dd（"2026-06-19"）；非法返回空串。 */
export function dateIntToIso(n: number | null | undefined): string {
  if (n == null) return "";
  const s = String(n);
  if (s.length !== 8) return "";
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

/** yyyy-MM-dd（"2026-06-19"）→ yyyyMMdd(int 20260619)；非法/空返回 null。 */
export function isoToDateInt(iso: string): number | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return Number(`${m[1]}${m[2]}${m[3]}`);
}
